import { agencyPrintLogoToDefinition } from "../../../../core/helpers/pdf-util.js";
import PdfColumnWidthVisitor from "./PdfColumnWidthVisitor.js";
import PdfRowRenderer from "./PdfRowRenderer.js";
import ReportDisclaimerPDFExporter from "../../../../core/pdfExport/ReportDisclaimerPDFExporter.js";
import ReportVisitor from "../../../../models/reports/reports/ReportVisitor.js";

/**
 * PDF exporter using the PDFMake doc definition.
 *
 * @param {company} spec.company - Company the report is for.
 * @param {object} spec.agency - Agency the company belongs to.
 * @param {polyglotObject} [spec.selectedDimensionsLabel] - String representing
 * the selected dimensions. If defined, this string is shown in the
 * document title.
 */
export default class PdfExportingVisitor extends ReportVisitor {
	constructor({
		company,
		reportDisclaimerTemplateVariables,
		selectedDimensionsLabel,
	}) {
		super(...arguments);
		this._company = company;

		this._selectedDimensionsLabel = selectedDimensionsLabel
			? selectedDimensionsLabel.toString()
			: "";

		this._reportDisclaimerExporter = new ReportDisclaimerPDFExporter({
			reportDisclaimerTemplateVariables,
			company,
		});

		this._definition = {
			table: {
				headerRows: 1,
				keepWithHeadRows: 1,
				body: [],
			},
			layout: this._getTableLayout(),
			style: "report",
		};

		this._printLogo = undefined;
	}

	async prepareAssets() {
		this._printLogo = await this._exportPrintLogo();
	}

	visitReport(report) {
		this._report = report;
		this._definition.table.widths = this._makeWidths();

		const columns = [""];
		columns.push(...this.visitAll(this._report.getVisibleColumns()));
		this._define(columns);

		this.visitAll(this._report.getVisibleRows());

		return {
			pageSize: "A4",
			title: this._report.getTitle().toString(),
			pageOrientation: this._getPageOrientation(),
			pageMargins: [20, 20, 20, 20],
			info: this._getMetadata(),
			footer: this._makeFooter(),
			content: [
				this._reportDisclaimerDefinition(),
				this._printLogo,
				{
					text: this._report.getTitle().toUpperCase(),
					style: "title",
				},
				{
					text: this._company.getName(),
					style: "companyName",
				},
				{
					text: this._selectedDimensionsLabel,
					style: "reportInfo",
				},
				this._definition,
			],
			styles: {
				...this._getStyles(),
				...this._reportDisclaimerExporter.getStyles(),
			},
		};
	}

	visitAbstractColumn(column) {
		return {
			text: column.getFullTitleString(),
			style: "tableHeader",
		};
	}

	visitAbstractRow(row) {
		this._rowRenderer().visit(row);
	}

	_rowRenderer() {
		return new PdfRowRenderer({
			report: this._report,
			definition: this._definition,
		});
	}

	_define(content) {
		this._definition.table.body.push(content);
	}

	_makeFooter() {
		return (pageNumber, numberOfPages) => ({
			text: `${pageNumber}/${numberOfPages}`,
			alignment: "center",
		});
	}

	_makeWidths() {
		const widths = ["*"];
		const visitor = new PdfColumnWidthVisitor();
		widths.push(...visitor.visitAll(this._report.getVisibleColumns()));
		return widths;
	}

	_getStyles() {
		return {
			title: {
				fontSize: 16,
				bold: true,
				color: "#222222",
				alignment: "center",
			},
			companyName: {
				fontSize: 12,
				color: "#222222",
				bold: true,
				margin: [0, 5],
				alignment: "center",
			},
			reportInfo: {
				fontSize: 8,
				color: "#222222",
				margin: [20, 0],
			},
			tag: {
				background: "#eeeeee",
				color: "#222222",
			},
			tableHeader: {
				bold: true,
				alignment: "right",
				margin: [0, 5],
			},
			tableSubheader: {
				bold: true,
				fontSize: 8,
			},
			report: {
				color: "#222222",
				fontSize: 8,
			},
			tableData: {
				alignment: "right",
			},
			tableFormulaData: {
				alignment: "right",
				bold: true,
			},
			accountLabel: {
				alignment: "left",
				color: "#222222",
				fontSize: 7,
			},
			accountDataCell: {
				alignment: "right",
				color: "#222222",
				fontSize: 7,
			},
			tableSum: {
				bold: true,
			},
			// FTGP-6375: TODO let's rename these styles when we polish pdf export
			row_heading: {
				bold: true,
				fontSize: 9,
			},
			row_subheading: {
				bold: true,
				fontSize: 8,
			},
			row_text: {
				fontSize: 8,
			},
			row_highlight: {
				italics: true,
				fontSize: 8,
				background: "#faf785",
			},
		};
	}

	_getMetadata() {
		// TODO FTGP-6016 set pdf metadata properly
		return {
			title: this._report.getTitle(),
			author: "Företagsplatsen AB",
		};
	}

	_getTableLayout() {
		// pdfmake's 'lightHorizontalLines' default layout with reduced padding
		// for top and bottom
		return {
			hLineWidth: (rowIndex, node) => {
				if (rowIndex === 0 || rowIndex === node.table.body.length) {
					return 0;
				}
				return rowIndex === node.table.headerRows ? 2 : 1;
			},
			vLineWidth: () => 0,
			hLineColor: (rowIndex) => (rowIndex === 1 ? "#222222" : "#aaa"),
			paddingLeft: (rowIndex) => (rowIndex === 0 ? 0 : 8),
			paddingRight: (rowIndex, node) =>
				rowIndex === node.table.widths.length - 1 ? 0 : 8,
			paddingTop: () => 1,
			paddingBottom: () => 1,
		};
	}

	_getPageOrientation() {
		return this._report.isLandscape() ? "landscape" : "portrait";
	}

	_reportDisclaimerDefinition() {
		return this._reportDisclaimerExporter.exportReportDisclaimer();
	}

	_getReportDisclaimer() {
		return this._company.getReportDisclaimer();
	}

	_exportPrintLogo() {
		const imgNode = document.querySelector(".report-agency-logo img");

		return agencyPrintLogoToDefinition(imgNode && imgNode.src);
	}
}
